/**
 * The rank-up's backdrop: the veil that covers the results, the light gathering
 * into the card before the swap, and the shockwave that recolours the screen
 * after it.
 *
 * One full-screen quad, one shader, every value read from the ceremony's frame —
 * so the recolour and the card's swap are the same instant by construction
 * rather than by two animations agreeing.
 */

import { Buffer, BufferUsage, Geometry, GlProgram, Mesh, Shader, UniformGroup } from 'pixi.js';

import { cssPalette } from '@/core/tokens';

const VERT = /* glsl */ `
in vec2 aPos;
out vec2 vUV;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uRect; // x, y, w, h in the container's pixels

void main() {
  vec2 p = uRect.xy + aPos * uRect.zw;
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(p, 1.0)).xy, 0.0, 1.0);
  vUV = aPos;
}
`;

const FRAG = /* glsl */ `
precision highp float;
in vec2 vUV;
out vec4 finalColor;

uniform vec3 uOld;      // the rank being left
uniform vec3 uNew;      // the rank being reached
uniform vec3 uVoid;     // the felt under both
uniform vec4 uState;    // veil, wipe, ring, flash
uniform vec4 uState2;   // rays, gather, time (s), aspect
uniform vec2 uCentre;   // the card's centre in uv
uniform vec4 uFloor;    // a glowing ring on the floor: centre x, y (uv), radius x, radius y (uv)
uniform float uFloorK;  // its strength; 0 draws none

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void main() {
  float veil = uState.x;
  float wipe = uState.y;
  float ring = uState.z;
  float flash = uState.w;
  float rays = uState2.x;
  float gather = uState2.y;
  float time = uState2.z;
  float aspect = uState2.w;

  vec2 d = (vUV - uCentre) * vec2(aspect, 1.0);
  float r = length(d);
  float ang = atan(d.y, d.x);

  // The farthest corner, so the wipe's 1.0 means "the whole screen".
  vec2 far = max(abs(vec2(0.0, 0.0) - uCentre), abs(vec2(1.0, 1.0) - uCentre)) * vec2(aspect, 1.0);
  float reach = length(far);

  // The veil: the rank's colour, deepest at the edges, over the felt.
  float fall = smoothstep(1.15, 0.05, r / reach);
  // Deep, not bright: the veil has to sit UNDER a white-hot card and a lit
  // face, so it carries the rank's hue at a fraction of its value. A veil as
  // bright as the card leaves the card nothing to be.
  vec3 oldVeil = mix(uVoid, uOld * 0.22, 0.3 + 0.45 * fall);
  vec3 newVeil = mix(uVoid, uNew * 0.22, 0.3 + 0.45 * fall);
  float swapped = wipe >= 0.0 ? smoothstep(wipe * reach + 0.012 * reach, wipe * reach - 0.012 * reach, r) : 0.0;
  vec3 col = mix(oldVeil, newVeil, swapped);
  vec3 rank = mix(uOld, uNew, swapped);

  // Rays: a slow fan out of the card, brighter as the heat builds.
  float fan = 0.5 + 0.5 * sin(ang * 9.0 + time * 0.55) * sin(ang * 4.0 - time * 0.31);
  float raysK = rays * fan * smoothstep(1.0, 0.1, r / reach) * 0.16;
  col += rank * raysK;

  // The gather: filaments drawn inward before the swap.
  if (gather > 0.001) {
    float spokes = pow(0.5 + 0.5 * sin(ang * 26.0 + hash(vec2(floor(ang * 8.0), 1.0)) * 6.28), 6.0);
    float pull = smoothstep(0.85, 0.05, r / reach) * spokes;
    col += mix(rank, vec3(1.0), 0.45) * pull * gather * 0.22;
  }

  // The shockwave's ring, riding the wipe.
  if (ring > 0.001) {
    // Not a thin white outline: a bright core with a chromatic fringe (the rank's own light split a hair), and a
    // broad soft halo of the rank's colour riding inside it.
    float band = r - wipe * reach;
    float core = exp(-pow(band / (0.026 * reach), 2.0));
    vec3 fringe = vec3(
      exp(-pow((band - 0.014 * reach) / (0.026 * reach), 2.0)),
      core,
      exp(-pow((band + 0.014 * reach) / (0.026 * reach), 2.0)));
    float broad = exp(-pow((band + 0.05 * reach) / (0.17 * reach), 2.0));
    col += (mix(rank, vec3(1.0), 0.55) * fringe * 0.62 + rank * broad * 0.4) * ring;
  }

  // The floor ring the chest stands in: an ellipse of the rank's light, a
  // bright rim and a soft pool inside it. Off (0) for the ceremony.
  if (uFloorK > 0.001) {
    vec2 q = (vUV - uFloor.xy) / max(uFloor.zw, vec2(0.0001));
    float d = length(q);
    float rim = exp(-pow((d - 1.0) / 0.07, 2.0));
    float pool = smoothstep(1.05, 0.0, d) * 0.35;
    col += mix(rank, vec3(1.0), 0.35) * (rim * 0.9 + pool) * uFloorK;
  }

  // The chest (floor ring on) flashes in the rarity it climbs to, so a climb
  // never whites out the screen; the ceremony keeps its warm white.
  vec3 flashCol = uFloorK > 0.001 ? mix(uNew, vec3(1.0), 0.2) : mix(vec3(1.0, 0.97, 0.9), rank, 0.4);
  col += flashCol * flash * (uFloorK > 0.001 ? 0.3 : 0.2);
  // A little grain, so a wide flat field of one colour does not band.
  col += (hash(vUV * 512.0 + time) - 0.5) * 0.012;

  finalColor = vec4(col, 1.0) * veil;
}
`;

let program: GlProgram | null = null;
let geometry: Geometry | null = null;

function quad(): Geometry {
  geometry ??= new Geometry({
    attributes: {
      aPos: {
        buffer: new Buffer({
          data: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
          usage: BufferUsage.VERTEX,
        }),
        format: 'float32x2',
      },
    },
    indexBuffer: new Buffer({
      data: new Uint16Array([0, 1, 2, 0, 2, 3]),
      usage: BufferUsage.INDEX | BufferUsage.COPY_DST,
    }),
  });
  return geometry;
}

function hexRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export interface BackdropFrame {
  readonly veil: number;
  /** The shockwave's radius as a fraction of the farthest corner; < 0 before the swap. */
  readonly wipe: number;
  readonly ring: number;
  readonly flash: number;
  readonly rays: number;
  readonly gather: number;
  /** Seconds on the ceremony's clock. */
  readonly time: number;
}

export class Backdrop extends Mesh<Geometry, Shader> {
  readonly #uniforms: UniformGroup;

  constructor(oldColour: string, newColour: string) {
    program ??= new GlProgram({ vertex: VERT, fragment: FRAG, name: 'w21-ceremony-backdrop' });
    const uniforms = new UniformGroup({
      uOld: { value: new Float32Array(hexRgb(oldColour)), type: 'vec3<f32>' },
      uNew: { value: new Float32Array(hexRgb(newColour)), type: 'vec3<f32>' },
      uVoid: { value: new Float32Array(hexRgb(cssPalette.void)), type: 'vec3<f32>' },
      uState: { value: new Float32Array([0, -1, 0, 0]), type: 'vec4<f32>' },
      uState2: { value: new Float32Array([0, 0, 0, 1]), type: 'vec4<f32>' },
      uCentre: { value: new Float32Array([0.5, 0.42]), type: 'vec2<f32>' },
      uFloor: { value: new Float32Array([0.5, 0.6, 0.3, 0.05]), type: 'vec4<f32>' },
      uFloorK: { value: 0, type: 'f32' },
      uRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
    });
    super({ geometry: quad(), shader: new Shader({ glProgram: program, resources: { backdropUniforms: uniforms } }) });
    this.#uniforms = uniforms;
  }

  /** The screen, in the container's pixels, and where the card sits on it. */
  layout(width: number, height: number, centreX: number, centreY: number): void {
    const rect = this.#uniforms.uniforms.uRect as Float32Array;
    rect[0] = 0;
    rect[1] = 0;
    rect[2] = width;
    rect[3] = height;
    const centre = this.#uniforms.uniforms.uCentre as Float32Array;
    centre[0] = centreX / Math.max(1, width);
    centre[1] = centreY / Math.max(1, height);
    const s2 = this.#uniforms.uniforms.uState2 as Float32Array;
    s2[3] = Math.max(0.01, width / Math.max(1, height));
    this.#uniforms.update();
  }

  /**
   * Change the two colours: the one being left and the one being reached. The
   * chest climbs through four, so it re-points the wipe at every step.
   */
  setColours(oldColour: string, newColour: string): void {
    const o = this.#uniforms.uniforms.uOld as Float32Array;
    const n = this.#uniforms.uniforms.uNew as Float32Array;
    const [or, og, ob] = hexRgb(oldColour);
    const [nr, ng, nb] = hexRgb(newColour);
    o[0] = or; o[1] = og; o[2] = ob;
    n[0] = nr; n[1] = ng; n[2] = nb;
    this.#uniforms.update();
  }

  /** The floor ring, in the container's pixels. `strength` 0 turns it off. */
  floor(x: number, y: number, rx: number, ry: number, width: number, height: number, strength: number): void {
    const f = this.#uniforms.uniforms.uFloor as Float32Array;
    f[0] = x / Math.max(1, width);
    f[1] = y / Math.max(1, height);
    f[2] = rx / Math.max(1, width);
    f[3] = ry / Math.max(1, height);
    this.#uniforms.uniforms.uFloorK = strength;
    this.#uniforms.update();
  }

  /** Where the rays and the wipe come from, in the container's pixels. */
  centre(x: number, y: number, width: number, height: number): void {
    const c = this.#uniforms.uniforms.uCentre as Float32Array;
    c[0] = x / Math.max(1, width);
    c[1] = y / Math.max(1, height);
    this.#uniforms.update();
  }

  draw(f: BackdropFrame): void {
    const s = this.#uniforms.uniforms.uState as Float32Array;
    s[0] = f.veil;
    s[1] = f.wipe;
    s[2] = f.ring;
    s[3] = f.flash;
    const s2 = this.#uniforms.uniforms.uState2 as Float32Array;
    s2[0] = f.rays;
    s2[1] = f.gather;
    s2[2] = f.time;
    this.#uniforms.update();
    this.visible = f.veil > 0.002;
  }
}
